import React from 'react';
import _ from 'lodash';
import CloseIcon from '@mui/icons-material/Close';
import moment, { MomentInput } from 'moment';
import { laTimezone } from 'services/helpers/data';
import { GridCellParams, GridColumnHeaderParams, GridColumns } from '@mui/x-data-grid-pro';
import { useSegment } from 'common/hooks/segment';
import { Divider } from '@mui/material';
import PageTitle from './Components/PageTitle';
import {
  getVesselScheduleEnd,
  VesselEntry,
  VesselSchedule,
} from '../../services/clients/vesselSchedule';
import VesselTable from './Components/VesselTable';
import styles from '../../styles/typography.module.css';

const DATE_FORMAT = 'YYYY-MM-DD HH:MM';

/** How long to wait before executing the search callback, in milliseconds */
const SEARCH_DEBOUNCE = 500;

/**
 * A vessel row consists of all the camel case and processed fields of vessel data
 * with their values.
 * TODO: We should figure out what to do about any here
 */ // eslint-disable-next-line @typescript-eslint/no-explicit-any
type VesselRow = Record<string, any>;

function valueIsNew(lastUpdate: MomentInput) {
  const utcLastUpdate = moment.utc(lastUpdate);
  const utcNow = moment.utc();
  const outdatedHours = moment(utcNow).diff(utcLastUpdate, 'hours');
  return outdatedHours < 23;
}

/**
 * Convert a vessel entry into a format that we can display in our table, which means turning
 * fields into camel case and combining date + time fields into single fields
 * @param inputRow the vessel entry to process
 * @param terminalId the terminal id string
 * @returns a processed vessel entry
 */
function transformVessselRow(inputRow: VesselEntry, terminalId: string): VesselRow {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { data: inputRowData, last_update } = inputRow;

  const lastUpdate = last_update ? laTimezone(last_update).format(DATE_FORMAT) : '';

  const camelCasedRow = Object.keys(inputRowData).reduce(
    (acc: VesselRow, key) => {
      // Intentionally disable the line since we're turning this into camel case
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { previous_value, current_value } = inputRowData[key];

      const propertyName = _.camelCase(key);
      acc[propertyName] = current_value;
      // For the dot
      acc[`${propertyName}PreviousValue`] = previous_value;
      acc.id = Math.random();
      return acc;
    },
    { terminalId, lastUpdate, isNew: valueIsNew(last_update) }
  );

  const DATE_AND_TIME_SEPARATE_PROPS = [
    'dryFirstReceiving',
    'reeferFirstReceiving',
    'dryCutOff',
    'reeferCutOff',
  ];

  DATE_AND_TIME_SEPARATE_PROPS.forEach((prop) => {
    // If we have both date & time
    if (camelCasedRow[`${prop}Date`] && camelCasedRow[`${prop}Time`]) {
      camelCasedRow[prop] = `${camelCasedRow[`${prop}Date`]} ${camelCasedRow[`${prop}Time`]}`;
      return;
    }

    // If we have just the date
    if (camelCasedRow[`${prop}Date`]) {
      camelCasedRow[prop] = camelCasedRow[`${prop}Date`];
    }
  });

  return camelCasedRow;
}

/**
 * Convert the backend response to a format that our table can display; do this by calling
 * transformVesselRow for each entry
 * @param backendResponse the response to processed
 * @returns the processed response which consists of a key value object
 */
function transformVesselSchedule(backendResponse: VesselSchedule): VesselRow[] {
  return Object.keys(backendResponse).reduce((acc: VesselRow[], terminalId) => {
    backendResponse[terminalId].forEach((row) => {
      const result = transformVessselRow(row, terminalId);
      acc.push(result);
    });
    return acc;
  }, []);
}

/**
 * Renders a single cell in our VesselSchedule table; this is expected to be used in
 * conjunction with mui and expects a GridCellParams
 * https://mui.com/api/data-grid/grid-cell-params/
 */
const VesselScheduleCell: React.FC<GridCellParams> = ({ formattedValue }) => (
  <div className="flex flex-col items-start py-4 leading-none text-dark p-2">
    <h5 className="font-medium">{formattedValue || '-'}</h5>
  </div>
);
VesselScheduleCell.displayName = 'VesselSchedule.Cell';

/**
 * Renders a header cell for our VesselSchedule table; expected to be used in conjunction
 * with mui and takes an optional GridColumnHeaderParams as props
 */
const VesselScheduleHeader: React.FC<
  Partial<GridColumnHeaderParams> & { children?: React.ReactNode }
> = ({ children, colDef = {} }) => {
  const { headerName } = colDef;
  return (
    <h6 className="text-dark uppercase font-bold tracking-wider leading-4 p-2">
      {children || headerName}
    </h6>
  );
};
VesselScheduleHeader.displayName = 'VesselSchedule.Header';

const columns: GridColumns = [
  {
    field: 'vesselName',
    headerName: 'Vessel Name',
    sortable: true,
    renderHeader: VesselScheduleHeader,
    minWidth: 128,
    width: 210,
    renderCell: (params: { row: { vesselName?: string } }) => (
      <div className="flex flex-col items-start px-2 py-4 leading-none">
        <h5 className="text-dark font-bold">
          {params.row.vesselName ? params.row.vesselName : '-'}
        </h5>
      </div>
    ),
    cellClassName: 'bg-white',
  },
  {
    field: 'vesselImportName',
    sortable: true,
    headerName: 'Inbount Vessel Voyage',
    renderHeader: () => <VesselScheduleHeader>Inbound Vessel Voyage</VesselScheduleHeader>,
    minWidth: 96,
    width: 225,
    renderCell: VesselScheduleCell,
    cellClassName: 'bg-white',
  },
  {
    field: 'vesselExportName',
    sortable: true,
    headerName: 'Outbound Vessel Voyage',
    renderHeader: () => <VesselScheduleHeader>Outbound Vessel Voyage</VesselScheduleHeader>,
    minWidth: 96,
    width: 230,
    renderCell: VesselScheduleCell,
    cellClassName: 'bg-white',
  },
  {
    field: 'terminal',
    sortable: true,
    headerName: 'Terminal',
    renderHeader: VesselScheduleHeader,
    minWidth: 96,
    width: 130,
    renderCell: VesselScheduleCell,
    cellClassName: 'bg-white',
    valueFormatter: (params) => params.value?.toString().toUpperCase(),
  },
  {
    field: 'estimatedArrivalDate',
    sortable: true,
    headerName: 'Estimated Arrival',
    renderHeader: () => <VesselScheduleHeader>Estimated Arrival</VesselScheduleHeader>,
    minWidth: 128,
    width: 190,
    renderCell: VesselScheduleCell,
    cellClassName: 'bg-white',
  },
  {
    field: 'actualArrivalDate',
    sortable: true,
    headerName: 'Actual Arrival',
    renderHeader: () => <VesselScheduleHeader>Actual Arrival</VesselScheduleHeader>,
    minWidth: 128,
    width: 175,
    renderCell: VesselScheduleCell,
    cellClassName: 'bg-white',
  },
  {
    field: 'estimatedDepartureDate',
    sortable: true,
    headerName: 'Estimated Departure',
    renderHeader: () => <VesselScheduleHeader>Estimated Departure</VesselScheduleHeader>,
    minWidth: 128,
    width: 210,
    renderCell: VesselScheduleCell,
    cellClassName: 'bg-white',
  },
  {
    field: 'actualDepartureDate',
    headerName: 'Actual Departure',
    sortable: true,
    renderHeader: () => <VesselScheduleHeader>Actual Departure</VesselScheduleHeader>,
    minWidth: 128,
    width: 190,
    renderCell: VesselScheduleCell,
    cellClassName: 'bg-white',
  },
  {
    field: 'dryFirstReceiving',
    sortable: true,
    headerName: 'Dry First Receiving',
    renderHeader: () => <VesselScheduleHeader>Dry First Receiving</VesselScheduleHeader>,
    minWidth: 128,
    width: 195,
    renderCell: VesselScheduleCell,
    cellClassName: 'bg-white',
  },
  {
    field: 'dryCutOff',
    headerName: 'Dry Cutoff',
    sortable: true,
    renderHeader: () => <VesselScheduleHeader>Dry Cutoff</VesselScheduleHeader>,
    minWidth: 128,
    width: 140,
    renderCell: VesselScheduleCell,
    cellClassName: 'bg-white',
  },
  {
    field: 'reeferFirstReceiving',
    sortable: true,
    headerName: 'Reefer First Receiving',
    renderHeader: () => <VesselScheduleHeader>Reefer First Receiving</VesselScheduleHeader>,
    minWidth: 128,
    width: 220,
    renderCell: VesselScheduleCell,
    cellClassName: 'bg-white',
  },
  {
    field: 'reeferCutOff',
    sortable: true,
    headerName: 'Reefer Cutoff',
    renderHeader: () => <VesselScheduleHeader>Reefer Cutoff</VesselScheduleHeader>,
    minWidth: 144,
    width: 160,
    renderCell: VesselScheduleCell,
    cellClassName: 'bg-white',
  },
  {
    field: 'lastUpdate',
    sortable: true,
    headerName: 'Last Update',
    renderHeader: VesselScheduleHeader,
    minWidth: 256,
    renderCell: VesselScheduleCell,
    cellClassName: 'bg-white',
  },
];

interface Props {
  pinnedColumns?: boolean;
}

interface State {
  loading: boolean;
  rows: VesselRow[];
  visibleRows: VesselRow[];
  showClear: boolean;
  error?: boolean;
}

const VesselSchedulePage: React.FC<Props> = (props) => {
  const vesselInputRef = React.useRef<HTMLInputElement>(null);

  const [state, setState] = React.useState<State>({
    loading: true,
    rows: [],
    visibleRows: [],
    showClear: false,
  });

  const { track } = useSegment();

  // Retrieve data on mount
  React.useEffect(() => {
    const retrieveSchedule = async () => {
      try {
        const data = await getVesselScheduleEnd();
        const rows = transformVesselSchedule(data);
        setState((s) => ({
          ...s,
          rows,
          visibleRows: rows,
          loading: false,
        }));
      } catch (error) {
        setState((s) => ({ ...s, error: true }));
      }
    };
    retrieveSchedule();
  }, []);

  const handleInputChange = _.debounce((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (!value || value.trim() === '') {
      setState((s) => ({ ...s, visibleRows: s.rows, showClear: false }));
      return;
    }

    setState((s) => {
      // Only get rows that match the entered name
      const visibleRows = s.rows.filter(
        (row) => row.vesselName && row.vesselName.toLowerCase().includes(value.toLowerCase())
      );

      // Analytics
      track(
        'Vessel Schedule Searched',
        {
          vesselSearched: value,
        },
        { includeUser: true }
      );

      // Update the state
      return { ...s, visibleRows, showClear: true };
    });
  }, SEARCH_DEBOUNCE);

  const clearSearch = React.useCallback(() => {
    if (vesselInputRef.current) {
      vesselInputRef.current.value = '';
    }
    setState((s) => ({ ...s, visibleRows: s.rows, showClear: false }));
  }, [setState]);

  const { visibleRows, loading, error, showClear } = state;
  const { pinnedColumns = true } = props;

  if (error) return <b>Error</b>;

  return (
    <div className={`w-full h-full ${styles.font}`}>
      <PageTitle title="Vessel Schedule" />
      <Divider />
      <form
        noValidate
        className="w-full py-4 px-8 flex items-center justify-center rounded"
        onSubmit={(event) => event.preventDefault()}
      >
        <div className="relative w-full h-10 px-8 bg-white flex items-center border border-blue-300 rounded z-40">
          <div
            className={`absolute left-0 top-0 bottom-0 w-16 flex items-center justify-center border-r border-blue-300 rounded-l ${
              showClear && 'bg-blue-100'
            }`}
          >
            {showClear ? (
              <button type="button" onClick={clearSearch} className="flex space-x-1 items-center">
                <CloseIcon style={{ fontSize: 12 }} className="text-gray-800" />
                <h5 className="font-medium text-gray-800">Clear</h5>
              </button>
            ) : (
              <svg
                className="fill-current text-gray-700"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="inherit"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12.0024 9.6605L4 12.949L7.01051 21H16.9943L20.0048 12.949L12.0024 9.6605ZM6.24809 7.14793H11.3722V8.31435L6.24809 10.2019V7.14793ZM17.7567 10.2019L12.6326 8.31435V7.14793H17.7567V10.2019ZM11.3722 5.87648H8.22653V4H11.3722V5.87648ZM15.7783 5.87648H12.6326V4H15.7783V5.87648Z"
                />
              </svg>
            )}
          </div>
          <div className="px-12 h-full w-full flex items-center">
            <input
              ref={vesselInputRef}
              type="search"
              name="seach-vessels"
              placeholder="Search vessels"
              onChange={handleInputChange}
              className="w-full h-9 outline-none font-regular placeholder-gray-800"
            />
          </div>
        </div>
      </form>
      <VesselTable
        columns={columns}
        visibleRows={visibleRows}
        loading={loading}
        pinnedColumns={pinnedColumns}
      />
    </div>
  );
};

export default VesselSchedulePage;
